import { useCallback, useEffect, useRef, useState } from 'react';

import { useAuth } from '../context/AuthContext';
import { apiRequest } from '../services/api/apiClient';

export interface CohortAccessSummary {
  id: number;
  name: string;
  program_level: string | null;
  enabled_features: string[];
}

export interface MentorTrainingAccess {
  has_access: boolean;
  role: 'student' | 'instructor' | 'none';
  school_id: number | null;
  student_cohort: CohortAccessSummary | null;
  instructor_cohorts: CohortAccessSummary[];
}

const DEFAULT_ACCESS: MentorTrainingAccess = {
  has_access: false,
  role: 'none',
  school_id: null,
  student_cohort: null,
  instructor_cohorts: [],
};

const ACCESS_CACHE_TTL_MS = 5 * 60 * 1000;

let accessCache:
  | {
      key: string;
      value: MentorTrainingAccess;
      fetchedAt: number;
    }
  | null = null;

let sharedInFlight:
  | {
      key: string;
      promise: Promise<MentorTrainingAccess>;
    }
  | null = null;

function getCacheKey(user: ReturnType<typeof useAuth>['user']): string {
  if (!user) return 'authenticated';
  return String(user.id ?? user.email ?? user.school_id ?? 'authenticated');
}

function getCachedAccess(key: string, allowStale = false): MentorTrainingAccess | null {
  if (!accessCache || accessCache.key !== key) return null;
  if (allowStale || Date.now() - accessCache.fetchedAt < ACCESS_CACHE_TTL_MS) {
    return accessCache.value;
  }
  return null;
}

function setCachedAccess(key: string, value: MentorTrainingAccess) {
  accessCache = {
    key,
    value,
    fetchedAt: Date.now(),
  };
}

function clearMentorAccessCache() {
  accessCache = null;
  sharedInFlight = null;
}

/**
 * Decides whether to render the Mentor Coach Training surface for a user.
 *
 * - Authenticated B2C: never shown (school_id is null).
 * - B2B student: shown only if their active cohort has the
 *   `mentor_training` feature enabled.
 * - B2B school admin / mentor / super-admin: always shown so they can
 *   configure cohorts; the cohort picker shows only feature-enabled
 *   cohorts.
 */
export function useMentorTrainingAccess() {
  const { phase, user } = useAuth();
  const cacheKey = phase === 'authenticated' ? getCacheKey(user) : null;
  const [access, setAccess] = useState<MentorTrainingAccess>(() => (
    cacheKey ? getCachedAccess(cacheKey, true) ?? DEFAULT_ACCESS : DEFAULT_ACCESS
  ));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef<Promise<MentorTrainingAccess> | null>(null);

  const refresh = useCallback(async (options?: { force?: boolean }): Promise<MentorTrainingAccess> => {
    if (!cacheKey) {
      clearMentorAccessCache();
      setAccess(DEFAULT_ACCESS);
      setLoading(false);
      setError(null);
      return DEFAULT_ACCESS;
    }

    const cached = options?.force ? null : getCachedAccess(cacheKey);
    if (cached) {
      setAccess(cached);
      setLoading(false);
      setError(null);
      return cached;
    }

    if (sharedInFlight?.key === cacheKey) {
      inFlight.current = sharedInFlight.promise;
      setLoading(!getCachedAccess(cacheKey, true));
      return sharedInFlight.promise.then((data) => {
        setAccess(data);
        setLoading(false);
        return data;
      });
    }

    if (inFlight.current) return inFlight.current;
    setLoading(true);
    setError(null);
    const p = (async () => {
      try {
        const data = await apiRequest<MentorTrainingAccess>({
          endpoint: '/mentor-training/me/access',
          method: 'GET',
        });
        setCachedAccess(cacheKey, data);
        setAccess(data);
        return data;
      } catch (e: any) {
        setError(e?.message || String(e));
        const stale = getCachedAccess(cacheKey, true);
        setAccess(stale ?? DEFAULT_ACCESS);
        return stale ?? DEFAULT_ACCESS;
      } finally {
        setLoading(false);
        inFlight.current = null;
        if (sharedInFlight?.key === cacheKey) {
          sharedInFlight = null;
        }
      }
    })();
    inFlight.current = p;
    sharedInFlight = { key: cacheKey, promise: p };
    return p;
  }, [cacheKey]);

  useEffect(() => {
    if (!cacheKey) {
      clearMentorAccessCache();
      setAccess(DEFAULT_ACCESS);
      setLoading(false);
      setError(null);
      return;
    }

    const cached = getCachedAccess(cacheKey);
    if (cached) {
      setAccess(cached);
      setLoading(false);
      setError(null);
      return;
    }

    refresh();
  }, [cacheKey, refresh]);

  return { access, loading, error, refresh };
}
